import {
	type BoxProps,
	ScrollArea,
	SegmentedControl,
	type SegmentedControlItem,
	Skeleton,
	Stack,
	Table,
	Text,
	Tooltip,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Paper } from '../../../components/Paper';
import { queryWithFilter } from '../../../util/api/queryWithFilter';
import { formatToCurrency } from '../../../util/formatting';
import type { FilterDto } from '../../../util/types/FilterDto';
import type { ProductSaleDto } from '../../../util/types/ReceiptViewDto';
import { KeyedRow } from '../../../components/KeyedRow';

interface TopTenProps extends BoxProps {
	filter: FilterDto;
}

const options: SegmentedControlItem[] = [
	{ value: 'netSales', label: 'Sales' },
	{ value: 'soldAmount', label: 'Amount' },
];

export function TopTenProducts({ filter, ...boxProps }: TopTenProps) {
	const { organisation } = useParams();
	const [isReady, setIsReady] = useState(false);

	useEffect(() => {
		setIsReady(true);
	}, []);

	const [displayOption, setDisplayOption] = useState(options[0].value);

	const topTenQuery = queryWithFilter<ProductSaleDto[]>(
		`${organisation}/dashboard/${displayOption}/top-ten-products`,
		filter,
		isReady,
	);

	return (
		<Paper
			title={'TOP TEN PRODUCTS'}
			headerContent={
				<SegmentedControl
					value={displayOption}
					onChange={setDisplayOption}
					data={options}
					mb={'xs'}
				/>
			}
		>
			<Stack h={'90%'}>
				<Skeleton
					visible={topTenQuery.isLoading || !topTenQuery.isSuccess}
					h={boxProps.h}
				>
					<ScrollArea h={boxProps.h}>
						<Table stickyHeader>
							<Table.Thead>
								<KeyedRow>
									<Table.Th>Name</Table.Th>
									<Table.Th>Net Sales</Table.Th>
									<Table.Th>Amount Sold</Table.Th>
								</KeyedRow>
							</Table.Thead>
							<Table.Tbody>
								{topTenQuery.data?.value.map((topTen) => (
									<KeyedRow>
										<Table.Td>
											<Tooltip label={topTen.name}>
												<Text lineClamp={1}>{topTen.name}</Text>
											</Tooltip>
										</Table.Td>
										<Table.Td>{formatToCurrency(topTen.netSales)}</Table.Td>
										<Table.Td>{topTen.soldCount}</Table.Td>
									</KeyedRow>
								))}
							</Table.Tbody>
						</Table>
					</ScrollArea>
				</Skeleton>
			</Stack>
		</Paper>
	);
}
